import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Eye, ChevronDown, Check, Building2, Swords } from 'lucide-react';
import { cn } from '../lib/cn';
import { api, type Role } from '../lib/api';
import type { ViewAsClanRank, ViewAsOrgRank, ViewAsState } from '../lib/viewAs';

// ─────────────────────────────────────────────────────────────────────────────
// ViewAsSwitcher
// Founder-only. Two SCOPED persona pickers (org + clan) — the ones that carry
// a real placement — above the remaining bare platform-role previews.
//
// Why scoped pickers dominate this panel (role-spine, roleSpine.ts): rank is
// resolved AT a placement. The same person is ORG_OWNER at org A and a total
// stranger at org B; a captain of team 3 has zero standing over team 4. A
// bare role label cannot express any of that, so it cannot preview it — which
// is how ORG_OWNER's exclusive capabilities (org.view_billing,
// org.manage_billing, org.manage_roles) went un-previewed entirely, and why
// the old bare "Clan Leader" option proved nothing about clan scoping.
//
// House language (12-portal-design-overhaul): no borders — raised fills +
// space; accent only on the active selection; the active "simulating" trigger
// uses a restrained red tint (semantic), no border, no glow. The hairline
// dividers below (border-b) are the same restrained convention Layout.tsx's
// footer uses — not decorative borders.
// ─────────────────────────────────────────────────────────────────────────────

const PANEL = 'rounded-lg bg-paper-panel shadow-[0_12px_32px_-6px_rgba(0,0,0,0.6)]';
const SELECT = 'input min-w-0 flex-1 !py-1.5 !text-xs';
const ENTER_BTN = 'btn-primary shrink-0 !px-2.5 !py-1.5 !text-[10px] disabled:opacity-50';
const BLOCK_TITLE = 'font-display text-[10px] font-black uppercase tracking-[0.22em] text-ink-secondary';
const BLOCK_BLURB = 'mt-1 font-mono text-[9px] uppercase tracking-wider text-ink-meta';
const ACTIVE_LINE = 'mt-2 flex items-center gap-1 font-mono text-[9px] uppercase tracking-wider text-red-300/80';

// Bare platform-role previews — the ones with genuinely NO placement behind
// them. The retired platform ADMIN tier is intentionally absent (admin is
// org-scoped now: see the Org block), so is the retired 'helper' role, and so
// is the old bare 'leader' option — a leader with no clan could not exercise
// any clan-scoped surface, so it was a label pretending to be a persona. Use
// the Clan block for that.
const ROLES: { value: Role; label: string; blurb: string }[] = [
  { value: 'member', label: 'Member', blurb: 'Regular player view' },
  { value: 'public', label: 'Public', blurb: 'Unauthenticated view' },
];

const ORG_RANKS: { value: ViewAsOrgRank; label: string }[] = [
  { value: 'ORG_ADMIN', label: 'Org Admin' },
  { value: 'ORG_OWNER', label: 'Org Owner' },
];

const CLAN_RANKS: { value: ViewAsClanRank; label: string }[] = [
  { value: 'CLAN_LEADER', label: 'Clan Leader' },
  { value: 'CLAN_CAPTAIN', label: 'Clan Captain' },
];

export function ViewAsSwitcher({
  current,
  onEnter,
  onEnterOrgAdmin,
  onEnterOrgOwner,
  onEnterClanLeader,
  onEnterClanCaptain,
}: {
  current: ViewAsState | null;
  onEnter: (role: Role) => void;
  onEnterOrgAdmin: (orgId: string, orgName: string) => void;
  onEnterOrgOwner: (orgId: string, orgName: string) => void;
  onEnterClanLeader: (clanId: string, clanName: string) => void;
  onEnterClanCaptain: (clanId: string, clanName: string, teamNo: number) => void;
}) {
  const [open, setOpen] = useState(false);
  const [orgRank, setOrgRank] = useState<ViewAsOrgRank>('ORG_ADMIN');
  const [orgPick, setOrgPick] = useState('');
  const [clanRank, setClanRank] = useState<ViewAsClanRank>('CLAN_LEADER');
  const [clanPick, setClanPick] = useState('');
  const [teamNo, setTeamNo] = useState('1');
  const ref = useRef<HTMLDivElement>(null);
  // Only fetch the pickers' lists once the panel is actually open — no reason
  // to pull every org and clan on every sidebar render for a control most
  // sessions never touch.
  const { data: orgs } = useQuery({ queryKey: ['orgs'], queryFn: api.orgs, enabled: open });
  const { data: clans } = useQuery({ queryKey: ['adminClans'], queryFn: api.admin.clans, enabled: open });

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Active state: a SCOPED sim wins the trigger label (it's the more specific
  // simulation) — a plain role sim falls back to the role list's own
  // highlight below.
  const activeScopeLabel = current?.org
    ? `Org: ${current.org.orgName}`
    : current?.clan
      ? `Clan: ${current.clan.clanName}`
      : null;

  const enterOrg = () => {
    const org = (orgs ?? []).find(o => o.id === orgPick);
    if (!org) return;
    if (orgRank === 'ORG_OWNER') onEnterOrgOwner(org.id, org.name);
    else onEnterOrgAdmin(org.id, org.name);
    setOpen(false);
    setOrgPick('');
  };

  const enterClan = () => {
    const clan = (clans ?? []).find(c => c.id === clanPick);
    if (!clan) return;
    if (clanRank === 'CLAN_CAPTAIN') {
      // Fail-closed on a junk team box rather than minting a captain pinned
      // to team NaN — a captain always belongs to exactly one real team.
      const parsed = Number.parseInt(teamNo, 10);
      if (!Number.isFinite(parsed) || parsed < 1) return;
      onEnterClanCaptain(clan.id, clan.name, parsed);
    } else {
      onEnterClanLeader(clan.id, clan.name);
    }
    setOpen(false);
    setClanPick('');
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        title="Founder view-as (UI only)"
        className={cn(
          'flex w-full items-center justify-between gap-1.5 rounded-lg px-2.5 py-2 font-display text-[10px] font-black uppercase tracking-wider transition-colors',
          current
            ? 'bg-red-500/10 text-red-200 hover:bg-red-500/[0.16]'
            : 'bg-white/[0.05] text-ink-secondary hover:bg-white/[0.08] hover:text-ink-primary'
        )}
      >
        <span className="flex min-w-0 items-center gap-1.5">
          <Eye className="h-3 w-3 shrink-0" strokeWidth={2.5} />
          <span className="truncate">{activeScopeLabel ?? 'View as'}</span>
        </span>
        <ChevronDown
          className={cn('h-3 w-3 shrink-0 transition-transform', open && 'rotate-180')}
          strokeWidth={2.5}
        />
      </button>

      {open && (
        <div className={cn('absolute bottom-full left-0 right-0 z-[60] mb-2 max-h-[70vh] overflow-y-auto', PANEL)}>
          <div className="px-3 py-2.5">
            <p className={BLOCK_TITLE}>Founder Tool</p>
            <p className={BLOCK_BLURB}>UI-only · does not affect server data</p>
          </div>

          {/* ORG-SCOPED — the first rung below Founder, and the only honest
              way to see what a real org manager sees: a founder's own view has
              cross-org reach no org manager has, so it cannot stand in for
              one. The rank select is not cosmetic — ORG_OWNER exclusively
              holds org.view_billing / org.manage_billing / org.manage_roles
              (src/shared/roleSpine.ts), so ORG_ADMIN cannot preview them. */}
          <div className="border-b border-white/[0.06] px-3 py-2.5">
            <p className={BLOCK_TITLE}>Org (Founder Preview)</p>
            <p className={BLOCK_BLURB}>
              Org roles are scoped: a real org admin or owner only ever sees their own org, never across orgs. Owner
              additionally holds billing and role management.
            </p>
            <div className="mt-2 flex gap-1.5">
              <select
                aria-label="Org rank to preview"
                value={orgRank}
                onChange={e => setOrgRank(e.target.value as ViewAsOrgRank)}
                className={SELECT}
              >
                {ORG_RANKS.map(r => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
              <select
                aria-label="Org to preview"
                value={orgPick}
                onChange={e => setOrgPick(e.target.value)}
                className={SELECT}
              >
                <option value="">Pick an org…</option>
                {(orgs ?? []).map(o => (
                  <option key={o.id} value={o.id}>{o.name}</option>
                ))}
              </select>
              <button aria-label="Enter org preview" onClick={enterOrg} disabled={!orgPick} className={ENTER_BTN}>
                Enter
              </button>
            </div>
            {current?.org && (
              <p className={ACTIVE_LINE}>
                <Building2 className="h-3 w-3 shrink-0" strokeWidth={2.5} />
                Viewing as: {current.orgRole === 'ORG_OWNER' ? 'Owner' : 'Admin'} of {current.org.orgName}
              </p>
            )}
          </div>

          {/* CLAN-SCOPED — CLAN_CAPTAIN is pinned to ONE team (roleSpine.ts:
              a captain of team 3 has zero standing over team 4), so a captain
              persona without a team number cannot exercise the rule it exists
              to test. A leader runs every team, so it takes no team. */}
          <div className="border-b border-white/[0.06] px-3 py-2.5">
            <p className={BLOCK_TITLE}>Clan (Founder Preview)</p>
            <p className={BLOCK_BLURB}>
              Clan roles are scoped to one clan. A captain is scoped further, to one team — pick the team to preview
              what they can and cannot touch.
            </p>
            <div className="mt-2 flex gap-1.5">
              <select
                aria-label="Clan rank to preview"
                value={clanRank}
                onChange={e => setClanRank(e.target.value as ViewAsClanRank)}
                className={SELECT}
              >
                {CLAN_RANKS.map(r => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
              <select
                aria-label="Clan to preview"
                value={clanPick}
                onChange={e => setClanPick(e.target.value)}
                className={SELECT}
              >
                <option value="">Pick a clan…</option>
                {(clans ?? []).map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              {clanRank === 'CLAN_CAPTAIN' && (
                <input
                  aria-label="Team number to preview"
                  type="number"
                  min={1}
                  value={teamNo}
                  onChange={e => setTeamNo(e.target.value)}
                  className="input w-14 shrink-0 !py-1.5 !text-xs"
                />
              )}
              <button aria-label="Enter clan preview" onClick={enterClan} disabled={!clanPick} className={ENTER_BTN}>
                Enter
              </button>
            </div>
            {current?.clan && (
              <p className={ACTIVE_LINE}>
                <Swords className="h-3 w-3 shrink-0" strokeWidth={2.5} />
                Viewing as: {current.clanRole === 'CLAN_CAPTAIN' ? 'Captain' : 'Leader'} of {current.clan.clanName}
                {current.clanRole === 'CLAN_CAPTAIN' && current.clan.teamNo != null && ` · team ${current.clan.teamNo}`}
              </p>
            )}
          </div>

          <div className="px-1.5 py-1.5">
            {ROLES.map(r => {
              const active = !current?.org && !current?.clan && current?.role === r.value;
              return (
                <button
                  key={r.value}
                  onClick={() => {
                    onEnter(r.value);
                    setOpen(false);
                  }}
                  className={cn(
                    'group flex w-full items-center justify-between gap-3 rounded-md px-3 py-2.5 text-left transition-colors',
                    active ? 'bg-white/[0.06]' : 'hover:bg-white/[0.04]'
                  )}
                >
                  <div className="min-w-0">
                    <p
                      className={cn(
                        'font-display text-xs font-black uppercase tracking-wider',
                        active ? 'text-accent-500' : 'text-ink-primary'
                      )}
                    >
                      {r.label}
                    </p>
                    <p className="mt-0.5 font-mono text-[9px] uppercase tracking-wider text-ink-meta">
                      {r.blurb}
                    </p>
                  </div>
                  {active && <Check className="h-3.5 w-3.5 shrink-0 text-accent-500" strokeWidth={3} />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
